import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { useAuth } from "@/context/auth.context";
import { motion } from "framer-motion";
import {
  Calendar, Clock, MapPin, Users, CheckCircle, FileText, Eye, Download,
  AlertTriangle, Link as LinkIcon, Paperclip, ListTodo, CalendarClock,
  UserCheck, Clock3, ChevronRight, CheckSquare, Ban, MessageSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";

const MEETING_TYPES: Record<string, { label: string; color: string }> = {
  general_assembly: { label: "General Assembly", color: "bg-blue-100 text-blue-800" },
  committee: { label: "Committee", color: "bg-purple-100 text-purple-800" },
  loan_review: { label: "Loan Review", color: "bg-amber-100 text-amber-800" },
  savings_review: { label: "Savings Review", color: "bg-green-100 text-green-800" },
  emergency: { label: "Emergency", color: "bg-red-100 text-red-800" },
};

const STATUS_CONFIG: Record<string, { color: string; label: string }> = {
  draft: { color: "bg-gray-100 text-gray-800", label: "Draft" },
  scheduled: { color: "bg-blue-100 text-blue-800", label: "Scheduled" },
  ongoing: { color: "bg-yellow-100 text-yellow-800", label: "Ongoing" },
  completed: { color: "bg-green-100 text-green-800", label: "Completed" },
  cancelled: { color: "bg-red-100 text-red-800", label: "Cancelled" },
};

const PRIORITY_CONFIG: Record<string, string> = {
  low: "bg-gray-100 text-gray-800",
  medium: "bg-blue-100 text-blue-800",
  high: "bg-amber-100 text-amber-800",
  urgent: "bg-red-100 text-red-800",
};

export default function MemberMeetings() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("upcoming");
  const [selectedMeeting, setSelectedMeeting] = useState<any>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isMinutesOpen, setIsMinutesOpen] = useState(false);
  const [isExcuseOpen, setIsExcuseOpen] = useState(false);
  const [excuseReason, setExcuseReason] = useState("");

  // ─── Data Fetching ───
  const { data: meetingsData, isLoading } = useQuery(
    ["member-meetings", user?.id],
    async () => {
      const { data } = await api.get(`/meetings/member/${user?.id}`);
      return data?.data || { upcoming: [], past: [], actionItems: [] };
    },
    { enabled: !!user?.id }
  );

  const upcoming = meetingsData?.upcoming || [];
  const past = meetingsData?.past || [];
  const actionItems = meetingsData?.actionItems || [];

  // ─── Mutations ───
  const confirmMutation = useMutation(
    async (meetingId: number) => {
      const { data } = await api.post(`/meetings/${meetingId}/confirm-attendance`, { teacherId: user?.id });
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["member-meetings"]);
        toast.success("Attendance confirmed");
      },
      onError: (e: any) => { toast.error(e.response?.data?.message || "Failed to confirm"); },
    }
  );

  const excuseMutation = useMutation(
    async ({ meetingId, excuse }: any) => {
      const { data } = await api.post(`/meetings/${meetingId}/request-excuse`, { teacherId: user?.id, excuse });
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["member-meetings"]);
        setIsExcuseOpen(false);
        setExcuseReason("");
        toast.success("Excuse request submitted");
      },
      onError: (e: any) => { toast.error(e.response?.data?.message || "Failed"); },
    }
  );

  const checkInMutation = useMutation(
    async ({ meetingId, checkInCode }: any) => {
      const { data } = await api.post(`/meetings/${meetingId}/check-in`, { teacherId: user?.id, checkInCode });
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["member-meetings"]);
        toast.success("Checked in successfully");
      },
      onError: (e: any) => { toast.error(e.response?.data?.message || "Check-in failed"); },
    }
  );

  // ─── Helpers ───
  const getAttendanceStatus = (meeting: any) => meeting.myAttendance?.status || "absent";
  const isAttending = (meeting: any) => {
    const s = getAttendanceStatus(meeting);
    return s === "present" || s === "late";
  };

  const printMinutes = (meeting: any) => {
    const logoEl = document.querySelector('[data-cooperative-logo]') as HTMLImageElement;
    const logoSrc = logoEl?.src || '';
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    printWindow.document.write(`
      <html><head><title>Minutes - ${meeting.meetingCode}</title>
      <style>body{font-family:Arial,sans-serif;padding:40px;max-width:800px;margin:0 auto}
      .header{text-align:center;border-bottom:3px solid #009A44;padding-bottom:15px;margin-bottom:25px}
      .content{line-height:1.8;white-space:pre-wrap}
      .field{margin:8px 0}.label{font-weight:bold;color:#333}.value{color:#555}</style></head><body>
      <div class="header">${logoSrc ? `<img src="${logoSrc}" style="height:50px">` : ''}
      <h2 style="margin:5px 0">MEETING MINUTES</h2>
      <p>${meeting.title} - ${meeting.meetingCode}</p></div>
      <div class="field"><span class="label">Date:</span> <span class="value">${format(new Date(meeting.meetingDate), "MMMM d, yyyy")}</span></div>
      <div class="field"><span class="label">Time:</span> <span class="value">${meeting.startTime} - ${meeting.endTime}</span></div>
      <div class="field"><span class="label">Location:</span> <span class="value">${meeting.location}</span></div>
      <hr style="margin:20px 0;border-color:#009A44">
      <div class="content">${meeting.minutes || "No minutes available."}</div>
      </body></html>`);
    printWindow.document.close();
    printWindow.print();
  };

  // ─── RENDER ───
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#009A44]" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
        <motion.div className="mb-4" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-lg sm:text-xl md:text-2xl font-bold tracking-tight" style={{ color: "#050500" }}>
            My Meetings
          </h1>
          <p className="text-sm text-gray-500 mt-1">View meetings, confirm attendance, and track action items</p>
        </motion.div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 mb-4">
            <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
            <TabsTrigger value="past">Past Meetings</TabsTrigger>
            <TabsTrigger value="attendance">My Attendance</TabsTrigger>
            <TabsTrigger value="actions">Action Items</TabsTrigger>
          </TabsList>

          {/* ═══ UPCOMING ═══ */}
          <TabsContent value="upcoming" className="space-y-3">
            {upcoming.length > 0 ? upcoming.map((meeting: any) => (
              <motion.div key={meeting.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="border rounded-lg p-4 bg-white hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs font-mono text-gray-500">{meeting.meetingCode}</span>
                      <Badge className={MEETING_TYPES[meeting.meetingType]?.color || "bg-gray-100"}>
                        {MEETING_TYPES[meeting.meetingType]?.label || meeting.meetingType}
                      </Badge>
                      <Badge className={STATUS_CONFIG[meeting.status]?.color || ""}>
                        {STATUS_CONFIG[meeting.status]?.label || meeting.status}
                      </Badge>
                    </div>
                    <h3 className="font-semibold text-sm sm:text-base">{meeting.title}</h3>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
                      <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{format(new Date(meeting.meetingDate), "EEEE, MMM d, yyyy")}</span>
                      <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{meeting.startTime} - {meeting.endTime}</span>
                      <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{meeting.location}</span>
                    </div>
                    {meeting.onlineLink && (
                      <a href={meeting.onlineLink} target="_blank" className="text-xs text-blue-600 underline flex items-center gap-1 mt-1">
                        <LinkIcon className="h-3 w-3" /> Join Online
                      </a>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {getAttendanceStatus(meeting) === "present" ? (
                      <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3.5 w-3.5 mr-1" />Confirmed</Badge>
                    ) : getAttendanceStatus(meeting) === "excused" ? (
                      <Badge className="bg-amber-100 text-amber-800"><Ban className="h-3.5 w-3.5 mr-1" />Excused</Badge>
                    ) : (
                      <div className="flex gap-1">
                        <Button size="sm" className="bg-[#009A44] hover:bg-[#008238] h-7 text-xs"
                          onClick={() => confirmMutation.mutate(meeting.id)}>
                          <CheckCircle className="h-3.5 w-3.5 mr-1" />Confirm
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs"
                          onClick={() => { setSelectedMeeting(meeting); setIsExcuseOpen(true); }}>
                          <Ban className="h-3.5 w-3.5 mr-1" />Excuse
                        </Button>
                      </div>
                    )}
                    <Button variant="ghost" size="sm" className="h-7 text-xs"
                      onClick={() => { setSelectedMeeting(meeting); setIsDetailOpen(true); }}>
                      View <ChevronRight className="h-3.5 w-3.5 ml-1" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            )) : (
              <Card><CardContent className="py-12 text-center text-gray-500">
                <CalendarClock className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No upcoming meetings</p>
              </CardContent></Card>
            )}
          </TabsContent>

          {/* ═══ PAST ═══ */}
          <TabsContent value="past" className="space-y-3">
            {past.length > 0 ? past.map((meeting: any) => (
              <motion.div key={meeting.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="border rounded-lg p-4 bg-white opacity-80">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs font-mono text-gray-500">{meeting.meetingCode}</span>
                      <Badge className={STATUS_CONFIG[meeting.status]?.color || ""}>{meeting.status}</Badge>
                      {getAttendanceStatus(meeting) === "present" && <Badge className="bg-green-100 text-green-800">Attended</Badge>}
                      {getAttendanceStatus(meeting) === "late" && <Badge className="bg-amber-100 text-amber-800">Late</Badge>}
                      {getAttendanceStatus(meeting) === "absent" && <Badge className="bg-red-100 text-red-800">Absent</Badge>}
                      {getAttendanceStatus(meeting) === "excused" && <Badge className="bg-gray-100 text-gray-800">Excused</Badge>}
                    </div>
                    <h3 className="font-semibold text-sm">{meeting.title}</h3>
                    <p className="text-xs text-gray-500 mt-1">{format(new Date(meeting.meetingDate), "MMM d, yyyy")} | {meeting.location}</p>
                  </div>
                  <div className="flex gap-1">
                    {meeting.minutesPublished && meeting.minutes && (
                      <Button variant="outline" size="sm" className="h-7 text-xs"
                        onClick={() => { setSelectedMeeting(meeting); setIsMinutesOpen(true); }}>
                        <FileText className="h-3.5 w-3.5 mr-1" />Minutes
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" className="h-7 text-xs"
                      onClick={() => { setSelectedMeeting(meeting); setIsDetailOpen(true); }}>
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            )) : (
              <Card><CardContent className="py-12 text-center text-gray-500">No past meetings</CardContent></Card>
            )}
          </TabsContent>

          {/* ═══ MY ATTENDANCE ═══ */}
          <TabsContent value="attendance" className="space-y-4">
            <Card>
              <CardHeader><CardTitle className="text-sm">Attendance Summary</CardTitle></CardHeader>
              <CardContent>
                {(() => {
                  const allMeetings = [...upcoming, ...past];
                  const total = allMeetings.length;
                  const attended = allMeetings.filter((m: any) => isAttending(m)).length;
                  const excused = allMeetings.filter((m: any) => getAttendanceStatus(m) === "excused").length;
                  const rate = total > 0 ? Math.round(((attended) / total) * 100) : 0;
                  return (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-blue-50 p-3 rounded-lg text-center">
                          <p className="text-2xl font-bold text-blue-600">{total}</p>
                          <p className="text-xs text-gray-500">Total Meetings</p>
                        </div>
                        <div className="bg-green-50 p-3 rounded-lg text-center">
                          <p className="text-2xl font-bold text-green-600">{attended}</p>
                          <p className="text-xs text-gray-500">Attended</p>
                        </div>
                        <div className="bg-amber-50 p-3 rounded-lg text-center">
                          <p className="text-2xl font-bold text-amber-600">{excused}</p>
                          <p className="text-xs text-gray-500">Excused</p>
                        </div>
                        <div className="bg-purple-50 p-3 rounded-lg text-center">
                          <p className="text-2xl font-bold text-purple-600">{rate}%</p>
                          <p className="text-xs text-gray-500">Attendance Rate</p>
                        </div>
                      </div>
                      <Progress value={rate} className="h-3" />
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            <div className="space-y-2">
              {past.map((meeting: any) => (
                <div key={meeting.id} className="flex items-center justify-between p-3 border rounded-lg bg-white">
                  <div>
                    <p className="text-sm font-medium">{meeting.title}</p>
                    <p className="text-xs text-gray-500">{format(new Date(meeting.meetingDate), "MMM d, yyyy")}</p>
                  </div>
                  <Badge className={
                    getAttendanceStatus(meeting) === "present" ? "bg-green-100 text-green-800" :
                    getAttendanceStatus(meeting) === "late" ? "bg-amber-100 text-amber-800" :
                    getAttendanceStatus(meeting) === "excused" ? "bg-gray-100 text-gray-800" :
                    "bg-red-100 text-red-800"
                  }>
                    {getAttendanceStatus(meeting)}
                  </Badge>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ═══ ACTION ITEMS ═══ */}
          <TabsContent value="actions" className="space-y-3">
            {actionItems.length > 0 ? actionItems.map((item: any) => (
              <Card key={item.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-mono text-gray-500">{item.itemCode}</span>
                        <Badge className={PRIORITY_CONFIG[item.priority]}>{item.priority}</Badge>
                        <Badge variant={item.status === "completed" ? "default" : "outline"}>{item.status}</Badge>
                      </div>
                      <p className="text-sm font-medium">{item.description}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Meeting: {item.meetingTitle} ({item.meetingCode})
                        {item.dueDate && ` | Due: ${format(new Date(item.dueDate), "MMM d, yyyy")}`}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )) : (
              <Card><CardContent className="py-12 text-center text-gray-500">
                <ListTodo className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No action items assigned to you</p>
              </CardContent></Card>
            )}
          </TabsContent>
        </Tabs>

      {/* ═══ DETAIL SHEET ═══ */}
      <Sheet open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{selectedMeeting?.title}</SheetTitle>
            <SheetDescription>{selectedMeeting?.meetingCode}</SheetDescription>
          </SheetHeader>
          {selectedMeeting && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-gray-400" /><span>{format(new Date(selectedMeeting.meetingDate), "MMM d, yyyy")}</span></div>
                <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-gray-400" /><span>{selectedMeeting.startTime} - {selectedMeeting.endTime}</span></div>
                <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-gray-400" /><span>{selectedMeeting.location}</span></div>
                <Badge className={MEETING_TYPES[selectedMeeting.meetingType]?.color}>{MEETING_TYPES[selectedMeeting.meetingType]?.label}</Badge>
              </div>
              <Badge className={STATUS_CONFIG[selectedMeeting.status]?.color}>{selectedMeeting.status}</Badge>
              {selectedMeeting.onlineLink && (
                <a href={selectedMeeting.onlineLink} target="_blank" className="text-sm text-blue-600 underline flex items-center gap-1">
                  <LinkIcon className="h-4 w-4" /> Join Online Meeting
                </a>
              )}
              {selectedMeeting.agenda && (
                <div><p className="text-sm font-medium mb-1">Agenda</p><p className="text-sm text-gray-600 whitespace-pre-wrap">{selectedMeeting.agenda}</p></div>
              )}
              <Separator />
              {selectedMeeting.minutes && selectedMeeting.minutesPublished && (
                <div>
                  <p className="text-sm font-medium mb-1">Meeting Minutes</p>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{selectedMeeting.minutes}</p>
                  <Button variant="outline" size="sm" className="mt-2" onClick={() => printMinutes(selectedMeeting)}>
                    <Download className="h-3.5 w-3.5 mr-1" /> Download Minutes
                  </Button>
                </div>
              )}
              {selectedMeeting.actionItems?.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Action Items</p>
                  {selectedMeeting.actionItems.filter((i: any) => i.assignedTo?.id === user?.id).map((item: any) => (
                    <div key={item.id} className="p-2 border rounded mb-2">
                      <p className="text-sm">{item.description}</p>
                      <p className="text-xs text-gray-500">Due: {item.dueDate ? format(new Date(item.dueDate), "MMM d") : "No date"} | {item.status}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* ═══ MINUTES DIALOG ═══ */}
      <Dialog open={isMinutesOpen} onOpenChange={setIsMinutesOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Meeting Minutes</DialogTitle>
            <DialogDescription>{selectedMeeting?.title} - {selectedMeeting?.meetingCode}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">{selectedMeeting?.minutes}</div>
            <Button variant="outline" onClick={() => printMinutes(selectedMeeting)}>
              <Download className="h-4 w-4 mr-2" /> Download / Print
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══ EXCUSE DIALOG ═══ */}
      <Dialog open={isExcuseOpen} onOpenChange={setIsExcuseOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request Excuse</DialogTitle>
            <DialogDescription>For: {selectedMeeting?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Reason for absence</Label>
              <Textarea value={excuseReason} onChange={(e) => setExcuseReason(e.target.value)} rows={3} placeholder="Please provide a reason..." />
            </div>
            <Button className="bg-[#009A44]" disabled={!excuseReason}
              onClick={() => excuseMutation.mutate({ meetingId: selectedMeeting?.id, excuse: excuseReason })}>
              Submit Excuse
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
